import { AbstractControl, FormControl, FormGroupDirective, NgForm, ValidationErrors, ValidatorFn } from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { TableFieldTypes } from "../../../locale/consts.en";
import { TableFieldType } from "./table-field.model";

/**
 * Material's default ErrorStateMatcher only shows a mat-error once the
 * control is dirty or touched - i.e. after the user has already interacted
 * with it (typed, or blurred). That hides a pre-existing bad expression
 * (loaded from an existing column/condition) until the user happens to
 * click into the field. This shows it as soon as it's actually invalid.
 */
export class ExpressionErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    _form: FormGroupDirective | NgForm | null,
  ): boolean {
    return !!control && control.invalid;
  }
}

/**
 * Validates a table "expression" column formula against the grammar the
 * mobile app actually evaluates it with at runtime:
 *   - service_app/lib/utils/math_expression_utils.dart (MathExpressionParser,
 *     wrapping the `math_expressions` Dart package's GrammarParser, plus a
 *     custom min()/max() rewrite)
 *   - service_app/lib/presentation/forms/widgets/equation_table_field.dart
 *     (ServiceEquationTableField, which strips "_" from both the equation
 *     and the bound variable names, and lets col1..col9/col0 stand in for
 *     the first ten columns - col_a..col_j)
 *
 * The mobile side silently swallows any parse/evaluate error (the field
 * just shows blank), so a typo here is otherwise invisible until someone
 * fills out the form on a device. This catches it at authoring time.
 */

// Unary functions built into math_expressions' lexer (lib/src/parser.dart) -
// case-sensitive, exactly one argument.
const UNARY_FUNCTIONS = [
  "sqrt",
  "ln",
  "cos",
  "sin",
  "tan",
  "arccos",
  "arcsin",
  "arctan",
  "abs",
  "ceil",
  "floor",
  "sgn",
  "e", // e(x) = exp(x), not the bare constant
];

// Two-argument functions built into the grammar: log(base, x), nrt(n, x).
const BINARY_FUNCTIONS = ["log", "nrt"];

// Added on top of the grammar by MathExpressionParser._processMinMaxFunctions -
// rewritten into plain arithmetic before parsing, but still exactly 2 args.
const CUSTOM_BINARY_FUNCTIONS = ["min", "max"];

const KNOWN_FUNCTIONS = new Set<string>([
  ...UNARY_FUNCTIONS,
  ...BINARY_FUNCTIONS,
  ...CUSTOM_BINARY_FUNCTIONS,
]);
const BINARY_ARITY_FUNCTIONS = new Set<string>([
  ...BINARY_FUNCTIONS,
  ...CUSTOM_BINARY_FUNCTIONS,
]);

// Only characters the mobile lexer can tokenize at all.
const ALLOWED_CHAR = /[A-Za-z0-9_.,(){}+\-*/%^!\s]/;
const IDENTIFIER_SOURCE = "[A-Za-z_][A-Za-z0-9_]*";

/** Strips "_" the same way the mobile app does before comparing names -
 *  ServiceEquationTableField/ServiceAssertionTableField both run
 *  `.replaceAll("_", "")` over the whole equation before parsing, so
 *  "col_a" and "cola" are genuinely the same reference on-device.
 *
 *  The mobile side also lets col1..col9/col0 stand in for col_a..col_j
 *  (replaceNumber()), but that alias isn't recognized here on purpose:
 *  a column must be referenced by its actual key, not that shorthand, so a
 *  typo like "col_1" is reported instead of silently matching "col_a". */
function normalizeIdentifier(token: string): string {
  return token.replace(/_/g, "");
}

/** Reads the parenthesized call starting at `openIndex` (which must point at
 *  "(" or "{") and returns its inner text plus the index right after the
 *  matching close bracket - or null if it's never closed. */
function readCall(
  expr: string,
  openIndex: number,
): { args: string; endIndex: number } | null {
  let depth = 0;
  for (let i = openIndex; i < expr.length; i++) {
    const ch = expr[i];
    if (ch === "(" || ch === "{") {
      depth++;
    } else if (ch === ")" || ch === "}") {
      depth--;
      if (depth === 0) {
        return { args: expr.slice(openIndex + 1, i), endIndex: i + 1 };
      }
    }
  }
  return null;
}

/** Splits a call's argument text on its top-level commas only, ignoring
 *  commas nested inside further parentheses. */
function splitTopLevelArgs(args: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < args.length; i++) {
    const ch = args[i];
    if (ch === "(" || ch === "{") {
      depth++;
    } else if (ch === ")" || ch === "}") {
      depth--;
    } else if (ch === "," && depth === 0) {
      parts.push(args.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(args.slice(start));
  return parts;
}

/** A column available for reference in an expression, as far as the
 *  validator needs to know about it. */
export interface ExpressionColumnInfo {
  key: string;
  type: TableFieldType;
  /** For a DROPDOWN column, or a RADIO/DROPDOWN form field: does it carry a
   *  numeric valueMap? That's the only way it is bindable as a number on the
   *  mobile side (table_dialog.dart's dropdownValueMaps, and
   *  DocumentFormulaScope for form fields). RADIO table columns get no
   *  numbers in this editor, so they stay non-numeric. */
  hasNumericValueMap?: boolean;
  /** For a DROPDOWN/RADIO: does *every* option have a numeric value, not
   *  just some of them? A column can be used in an expression as soon as
   *  one option has a number (hasNumericValueMap), but if someone later
   *  picks an option that was never mapped, the expression silently fails
   *  on-device - worth a heads-up even though it's not an outright error. */
  allValuesNumeric?: boolean;
}

// Types the mobile app actually binds as a `num` in the expression's
// context model (table_dialog.dart / widget_functions.dart numberFields,
// DocumentFormulaScope for form fields).
function isNumericColumn(col: ExpressionColumnInfo): boolean {
  if (col.type === TableFieldType.DECIMAL || col.type === TableFieldType.INTEGER) {
    return true;
  }
  return isOptionColumn(col) && !!col.hasNumericValueMap;
}

function isOptionColumn(col: ExpressionColumnInfo): boolean {
  return col.type === TableFieldType.DROPDOWN || col.type === TableFieldType.RADIO;
}

export interface ExpressionValidationResult {
  /** Blocking problems - the expression won't evaluate correctly on-device. */
  errors: string[];
  /** Non-blocking heads-up - the expression is fine today but is one
   *  reasonably likely edit away from silently breaking on-device. */
  warnings: string[];
}

/**
 * Checks an expression's parentheses, function names, function argument
 * counts, column references, and that every referenced column is actually
 * numeric. Returns human-readable problems - empty when the expression is
 * fine (or blank; required-ness is a separate validator).
 */
export function validateExpression(
  expression: string,
  availableColumns: ExpressionColumnInfo[],
): ExpressionValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const expr = (expression ?? "").trim();
  if (expr === "") {
    return { errors, warnings };
  }

  // 1. Character set.
  const strayChars = new Set<string>();
  for (const ch of expr) {
    if (!ALLOWED_CHAR.test(ch)) {
      strayChars.add(ch);
    }
  }
  if (strayChars.size > 0) {
    errors.push(
      $localize`Unexpected character(s): ${[...strayChars].join(" ")}`,
    );
  }

  // 2. Parenthesis balance - the grammar treats "{"/"}" as interchangeable
  // with "("/")", so they're counted together.
  let depth = 0;
  let unmatchedClose = false;
  for (const ch of expr) {
    if (ch === "(" || ch === "{") {
      depth++;
    } else if (ch === ")" || ch === "}") {
      depth--;
      if (depth < 0) {
        unmatchedClose = true;
        depth = 0;
      }
    }
  }
  if (unmatchedClose) {
    errors.push($localize`Unmatched closing parenthesis`);
  } else if (depth > 0) {
    errors.push($localize`Missing ${depth} closing parenthesis(es)`);
  }

  // 3. Known columns, normalized the same way the mobile app compares names.
  const knownColumns = new Map<string, ExpressionColumnInfo>();
  for (const col of availableColumns) {
    if (col.key) {
      knownColumns.set(normalizeIdentifier(col.key), col);
    }
  }

  // 4. Walk every identifier: a function name when followed by "(" / "{",
  // otherwise a column reference.
  const unknownFunctions = new Set<string>();
  const unknownColumns = new Set<string>();
  const nonNumericColumns = new Set<string>();
  const partiallyMappedDropdowns = new Set<string>();
  const arityErrors: string[] = [];

  const identifierRe = new RegExp(IDENTIFIER_SOURCE, "g");
  let match: RegExpExecArray | null;
  while ((match = identifierRe.exec(expr)) !== null) {
    const token = match[0];
    let i = identifierRe.lastIndex;
    while (i < expr.length && /\s/.test(expr[i])) {
      i++;
    }
    const isCall = expr[i] === "(" || expr[i] === "{";

    if (isCall) {
      if (!KNOWN_FUNCTIONS.has(token)) {
        unknownFunctions.add(token);
        continue;
      }
      const call = readCall(expr, i);
      if (call) {
        const argCount =
          call.args.trim() === "" ? 0 : splitTopLevelArgs(call.args).length;
        const expected = BINARY_ARITY_FUNCTIONS.has(token) ? 2 : 1;
        if (argCount !== expected) {
          arityErrors.push(
            $localize`"${token}" needs ${expected} argument(s), got ${argCount}`,
          );
        }
      }
    } else {
      const col = knownColumns.get(normalizeIdentifier(token));
      if (!col) {
        unknownColumns.add(token);
      } else if (!isNumericColumn(col)) {
        const typeLabel = TableFieldTypes[col.type] ?? String(col.type);
        nonNumericColumns.add(`${token} (${typeLabel})`);
      } else if (isOptionColumn(col) && !col.allValuesNumeric) {
        partiallyMappedDropdowns.add(token);
      }
    }
  }

  if (unknownFunctions.size > 0) {
    errors.push(
      $localize`Unknown function(s): ${[...unknownFunctions].join(", ")}`,
    );
  }
  if (unknownColumns.size > 0) {
    errors.push(
      $localize`Unknown column(s): ${[...unknownColumns].join(", ")}`,
    );
  }
  if (nonNumericColumns.size > 0) {
    errors.push(
      $localize`Not a number - needs a decimal/whole number column: ${[...nonNumericColumns].join(", ")}`,
    );
  }
  errors.push(...arityErrors);

  if (partiallyMappedDropdowns.size > 0) {
    warnings.push(
      $localize`Dropdown column(s) missing a numeric value for some options - the expression will fail on-device if that option is picked: ${[...partiallyMappedDropdowns].join(", ")}`,
    );
  }

  return { errors, warnings };
}

/** Reactive-forms validator wrapping validateExpression(). `getColumns` is
 *  read lazily on every check, since the set of sibling columns can change
 *  while this field is being edited. */
export function expressionSyntaxValidator(
  getColumns: () => ExpressionColumnInfo[],
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const { errors } = validateExpression(control.value, getColumns());
    return errors.length > 0 ? { expressionSyntax: errors } : null;
  };
}
